(() => {
  if (window.__yqDiscountToggleLoaded) return;
  window.__yqDiscountToggleLoaded = true;

  const STORAGE_KEY = 'yqDiscountToggle';
  const CONFLICT_KEY = 'yqDiscountToggleConflict';
  const RULE_CACHE_KEY = 'yqDiscountToggleRules';
  const CONFLICT_MESSAGE = 'Only 1 discount code per order.';
  const CARD_SEL = '[data-yq-discount-toggle-card]';
  const BUTTON_SEL = '[data-yq-discount-toggle]';

  let pageIntentEnabled = false;
  let lastCart = null;
  let frozenCatalogBases = null;
  let priceRefreshTimer = null;
  let priceRefreshEpoch = 0;
  let cartLock = Promise.resolve();
  let cartLockDepth = 0;
  let syncAfterLock = false;
  let syncSeq = 0;
  let syncTimer = 0;
  let queuedSyncCart = null;
  let queuedSyncHasCart = false;

  // Cart writes (probe, ATC, remove) must finish in order. keepalive updates
  // were landing after a later update and flipping the code on the storefront.
  const withCartLock = (fn) => {
    const run = cartLock.then(async () => {
      cartLockDepth += 1;
      try {
        return await fn();
      } finally {
        cartLockDepth -= 1;
        if (cartLockDepth === 0 && syncAfterLock) {
          syncAfterLock = false;
          window.setTimeout(() => {
            if (cartLockDepth === 0) requestSync();
          }, 0);
        }
      }
    });
    cartLock = run.then(() => {}, () => {});
    return run;
  };

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  };

  const writeState = (patch) => {
    const next = { ...readState(), ...patch };
    delete next.previewType;
    delete next.previewValue;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) { /* private mode */ }
    return next;
  };

  const setConflictMessage = (on) => {
    try {
      if (on) sessionStorage.setItem(CONFLICT_KEY, CONFLICT_MESSAGE);
      else sessionStorage.removeItem(CONFLICT_KEY);
    } catch (e) { /* ignore */ }
  };

  const getConflictMessage = () => {
    try {
      return sessionStorage.getItem(CONFLICT_KEY) || '';
    } catch (e) {
      return '';
    }
  };

  const rememberCart = (cart) => {
    if (cart) lastCart = cart;
    return lastCart;
  };

  const fetchCart = async () => {
    const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('cart');
    return rememberCart(await response.json());
  };

  const listedCodes = (cart) => {
    const list = cart?.discount_codes || [];
    return list.filter((item) => item && item.code).map((item) => String(item.code));
  };

  // Codes that currently reduce the cart (pass Shopify eligibility).
  const applicableCodes = (cart) => {
    const list = cart?.discount_codes || [];
    return list.filter((item) => item && item.applicable && item.code).map((item) => String(item.code));
  };

  const codesEqual = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

  const cartHasCode = (cart, code) => listedCodes(cart).some((c) => codesEqual(c, code));

  const readRuleCache = () => {
    try {
      return JSON.parse(sessionStorage.getItem(RULE_CACHE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  };

  const writeRuleCache = (patch) => {
    const next = { ...readRuleCache(), ...patch };
    try {
      sessionStorage.setItem(RULE_CACHE_KEY, JSON.stringify(next));
    } catch (e) { /* private mode */ }
    return next;
  };

  const ruleIsUsable = (rule) => {
    if (!rule) return false;
    const type = String(rule.value_type || '').toLowerCase();
    const value = parseFloat(rule.value);
    return (type === 'percentage' || type === 'fixed_amount') && Number.isFinite(value) && value > 0;
  };

  // fixed_amount `value` is currency units ("15.0" → 1500 cents). A cart that
  // already has other items reports a partial total_allocated_amount — never
  // treat that split as this product's discount.
  const fixedCentsFromRule = (rule) => {
    const stored = Math.round(Number(rule?.amount_cents));
    if (Number.isFinite(stored) && stored > 0) return stored;
    const raw = parseFloat(rule?.value);
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    const asMajor = Math.round(raw * 100);
    const asSub = Math.round(raw);
    if (String(rule.value).trim().includes('.')) return asMajor;
    const allocated = Math.max(0, Math.round(Number(rule.total_allocated_amount) || 0));
    if (allocated > 0 && Math.abs(allocated - asSub) <= 1 && Math.abs(allocated - asMajor) > 1) return asSub;
    return asMajor;
  };

  const cacheDiscountRule = (code, rule) => {
    if (!code || !ruleIsUsable(rule)) return null;
    const key = String(code).trim().toLowerCase();
    const payload = {
      value_type: rule.value_type,
      value: rule.value,
      title: rule.title || code,
      code: rule.code || code,
      total_allocated_amount: rule.total_allocated_amount
    };
    if (String(rule.value_type || '').toLowerCase() === 'fixed_amount') {
      const cents = fixedCentsFromRule(rule);
      if (cents > 0) payload.amount_cents = cents;
    }
    writeRuleCache({ [key]: payload });
    return payload;
  };

  const getCachedDiscountRule = (code) => {
    if (!code) return null;
    const cached = readRuleCache()[String(code).trim().toLowerCase()] || null;
    return ruleIsUsable(cached) ? cached : null;
  };

  const silentAddVariant = async (variantId) => {
    const body = new URLSearchParams();
    body.set('id', String(variantId));
    body.set('quantity', '1');
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body
    });
    if (!response.ok) throw new Error('cart-add');
    return fetchCart();
  };

  const cartChangeQuantity = async (lineKey, quantity) => {
    const response = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!response.ok) throw new Error('cart-change');
    return rememberCart(await response.json());
  };

  const dispatchCartEvents = (cart, baseEvent) => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent, cart }
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  };

  const updateCartDiscount = async (discount, { bundleSections = true } = {}) => {
    const sectionsToBundle = [];
    if (bundleSections) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));
    }

    const payload = { discount: discount || '' };
    if (bundleSections && sectionsToBundle.length) payload.sections = sectionsToBundle;

    const response = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('discount-update');
    return rememberCart(await response.json());
  };

  const getActiveCard = () => document.querySelector(`${CARD_SEL}[data-code]`);

  const getProductForm = (formId) => {
    // Prefer the live product-form (buy-buttons). Duplicate ids often leave a
    // hidden form first in document.getElementById order with a stale variant id.
    if (formId) {
      const escaped = CSS.escape(formId);
      return (
        document.querySelector(`form[is="product-form"]#${escaped}`) ||
        document.querySelector(`form[is="product-form"][id="${escaped}"]`) ||
        document.getElementById(formId) ||
        document.forms[formId] ||
        null
      );
    }
    return (
      document.querySelector('form[is="product-form"]') ||
      document.querySelector('form[id^="product-form-"]') ||
      null
    );
  };

  const getBuyTotalsFormId = (preferredId) => {
    if (preferredId) return preferredId;
    const form = getProductForm();
    if (form?.id) return form.id;
    return document.querySelector('[data-yq-buy-totals][data-product-form-id]')?.getAttribute('data-product-form-id') || '';
  };

  const getFormVariantId = (formId) => {
    const form = getProductForm(formId);
    if (!form) return null;
    const input =
      form.querySelector('[name="id"]:not([disabled])') ||
      form.querySelector('input[name="id"]');
    const id = parseInt(input?.value, 10);
    return Number.isFinite(id) ? id : null;
  };

  const getPageProductId = () => {
    const metaId = window.meta?.product?.id;
    if (Number.isFinite(Number(metaId))) return Number(metaId);
    const analyticsId = window.ShopifyAnalytics?.meta?.product?.id;
    if (Number.isFinite(Number(analyticsId))) return Number(analyticsId);
    return null;
  };

  const findCartItemsForSelection = (cart, variantId) => {
    const items = cart?.items || [];
    if (!items.length) return [];
    if (variantId) {
      const byVariant = items.filter((item) => Number(item.variant_id) === Number(variantId));
      if (byVariant.length) return byVariant;
    }
    const productId = getPageProductId();
    if (productId) {
      return items.filter((item) => Number(item.product_id) === Number(productId));
    }
    return [];
  };

  const getFormQuantity = (formId) => {
    const id = formId || getBuyTotalsFormId();
    const form = getProductForm(id);
    let qty = parseInt(form?.querySelector('[name="quantity"]')?.value, 10);
    // Quantity lives outside the form and links via the form="" attribute.
    if (!Number.isFinite(qty) || qty <= 0) {
      const linked = id
        ? document.querySelector(`input[name="quantity"][form="${CSS.escape(id)}"]`)
        : null;
      qty = parseInt(linked?.value, 10);
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      qty = parseInt(
        document.querySelector('.yq-inline-buy-row .quantity-selector__input, .yq-inline-buy-row [name="quantity"]')?.value,
        10
      );
    }
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  };

  const getAddonTotals = (formId) => {
    let addonPrice = 0;
    let addonCompare = 0;
    if (!formId) return { addonPrice, addonCompare };
    document.querySelectorAll(`yq-product-addons[data-product-form-id="${formId}"] [data-addon-card]`).forEach((card) => {
      if (!card.querySelector('.yq-product-addons__toggle.is-selected')) return;
      const selectedSwatch = card.querySelector('[data-addon-swatch].is-selected');
      const price = parseInt(selectedSwatch?.dataset.variantPriceCents || card.getAttribute('data-price'), 10) || 0;
      const compare = parseInt(selectedSwatch?.dataset.variantCompareCents || card.getAttribute('data-compare-price'), 10) || 0;
      addonPrice += price;
      if (compare > price) addonCompare += compare;
      else addonCompare += price;
    });
    return { addonPrice, addonCompare };
  };

  const isDiscountIntentOn = () => {
    // Only the on-page toggle controls PDP price rewriting.
    // Never fall back to sessionStorage alone — that would rewrite prices on
    // products/pages where the user has not turned the switch on.
    const card = getActiveCard();
    return !!(card && card.classList.contains('is-enabled'));
  };

  const getManagedCode = () => {
    const card = getActiveCard();
    const state = readState();
    return (card?.dataset.code || state.code || state.managedCode || '').trim();
  };

  const freezeCatalogBases = (formId) => {
    const bases = readCatalogBases(formId);
    if (bases.basePrice != null) frozenCatalogBases = bases;
  };

  const clearFrozenCatalogBases = () => {
    frozenCatalogBases = null;
  };

  const readCatalogBases = (formId) => {
    const buyRoot = formId
      ? document.querySelector(`[data-yq-buy-totals][data-product-form-id="${formId}"]`)
      : null;
    const pdpRoot = document.querySelector('[data-yq-pdp-price]');
    const root = pdpRoot || buyRoot;
    const basePrice = parseInt(root?.getAttribute('data-base-price'), 10);
    const baseCompare = parseInt(
      pdpRoot?.getAttribute('data-base-compare') || root?.getAttribute('data-base-compare'),
      10
    );
    return {
      basePrice: Number.isFinite(basePrice) ? basePrice : null,
      baseCompare: Number.isFinite(baseCompare) ? baseCompare : null
    };
  };

  const getPriceBases = (formId) => {
    if (frozenCatalogBases?.basePrice != null) return frozenCatalogBases;
    return readCatalogBases(formId);
  };

  /**
   * Find Shopify discount application for our code (has value / value_type).
   */
  const findCodeDiscountRule = (cart, code) => {
    if (!cart || !code) return null;
    const match = (app) => {
      if (!app) return false;
      return codesEqual(app.title, code) || codesEqual(app.code, code);
    };
    const pools = [
      cart.cart_level_discount_applications,
      cart.discount_applications
    ];
    for (const list of pools) {
      const hit = (list || []).find(match);
      if (hit) return hit;
    }
    for (const item of cart.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        if (match(alloc?.discount_application)) return alloc.discount_application;
      }
    }
    return null;
  };

  /**
   * Prefer live cart rule; fall back to session cache so PDP can price
   * while the code is not yet (or no longer) on the cart.
   */
  const resolveDiscountRule = (cart, code) => {
    if (!code) return null;
    if (cart && cartHasCode(cart, code)) {
      const live = findCodeDiscountRule(cart, code);
      if (live) {
        cacheDiscountRule(code, live);
        return live;
      }
    }
    return getCachedDiscountRule(code);
  };

  /**
   * Learn this product's discount rule for PDP pricing without leaving it on the cart.
   * If the code is not applicable yet (product not in cart), temporarily add one unit,
   * read the rule, then restore quantity and the previous cart discount.
   */
  const probeAndCacheDiscountRule = async (code) => {
    if (!code) return null;
    const cached = getCachedDiscountRule(code);
    if (cached) return cached;

    const before = await fetchCart();
    const previousDiscount = listedCodes(before).join(',');
    const variantId = getFormVariantId();
    const existing = (before.items || []).find((item) => Number(item.variant_id) === Number(variantId));
    const previousQty = existing ? existing.quantity : 0;
    let touchedLine = false;
    let probed = before;

    const restoreCart = async () => {
      if (touchedLine && variantId) {
        const now = probed?.items ? probed : await fetchCart();
        const line = (now.items || []).find((item) => Number(item.variant_id) === Number(variantId));
        if (line?.key && line.quantity !== previousQty) {
          probed = await cartChangeQuantity(line.key, previousQty);
        }
      }
      const currentCodes = listedCodes(probed?.discount_codes ? probed : await fetchCart()).join(',');
      if (currentCodes !== previousDiscount) {
        probed = await updateCartDiscount(previousDiscount, { bundleSections: false });
      }
    };

    try {
      probed = await updateCartDiscount(code, { bundleSections: false });
      let live = findCodeDiscountRule(probed, code);

      if (!ruleIsUsable(live) && variantId) {
        probed = await silentAddVariant(variantId);
        touchedLine = true;
        probed = await updateCartDiscount(code, { bundleSections: false });
        live = findCodeDiscountRule(probed, code);
      }

      if (ruleIsUsable(live)) cacheDiscountRule(code, live);
      await restoreCart();
      return ruleIsUsable(live) ? cacheDiscountRule(code, live) || live : getCachedDiscountRule(code);
    } catch (e) {
      try {
        await restoreCart();
      } catch (restoreErr) { /* ignore */ }
      return getCachedDiscountRule(code);
    }
  };

  /**
   * Coupon off for THIS product from the real Shopify discount application only.
   * percentage → catalogSale × pct; fixed_amount → code value in cents.
   */
  const couponOffFromDiscountRule = (catalogSale, rule) => {
    if (!rule) return { couponOff: 0, percent: 0 };

    const type = String(rule.value_type || '').toLowerCase();
    if (type === 'percentage') {
      const pct = parseFloat(rule.value);
      if (Number.isFinite(pct) && pct > 0) {
        return {
          couponOff: Math.min(catalogSale, Math.round((catalogSale * pct) / 100)),
          percent: Math.round(pct)
        };
      }
    }

    if (type === 'fixed_amount') {
      const fixed = fixedCentsFromRule(rule);
      if (fixed > 0) {
        return {
          couponOff: Math.min(catalogSale, fixed),
          percent: 0
        };
      }
    }

    // Rule present but value unreadable — use allocated amount for this code once.
    const allocated = Math.max(0, Math.round(Number(rule.total_allocated_amount) || 0));
    if (allocated > 0) {
      return { couponOff: Math.min(catalogSale, allocated), percent: 0 };
    }

    return { couponOff: 0, percent: 0 };
  };

  /**
   * sale = catalogSale − couponOff (once), from the real cart discount application
   * or a cached rule (toggle ON before ATC).
   */
  const deriveDisplayPricing = (formId, qty, cart) => {
    if (!isDiscountIntentOn()) return null;
    const code = getManagedCode();
    if (!code) return null;

    const { basePrice, baseCompare } = getPriceBases(formId);
    if (!Number.isFinite(basePrice)) return null;

    const safeQty = Math.max(1, Math.round(Number(qty) || 1));
    const catalogSale = basePrice * safeQty;
    const catalogCompare =
      Number.isFinite(baseCompare) && baseCompare > basePrice ? baseCompare * safeQty : catalogSale;

    const rule = resolveDiscountRule(cart, code);
    const fromRule = couponOffFromDiscountRule(catalogSale, rule);
    if (fromRule.couponOff <= 0) return null;

    const couponOff = Math.max(0, Math.min(fromRule.couponOff, catalogSale));
    const saleCents = Math.max(0, catalogSale - couponOff);
    const originalCents = Math.max(catalogCompare, saleCents);
    const savingsCents = Math.max(0, originalCents - saleCents);
    const percent =
      fromRule.percent > 0
        ? fromRule.percent
        : originalCents > 0
          ? Math.round((savingsCents / originalCents) * 100)
          : 0;
    if (savingsCents <= 0) return null;

    return { saleCents, originalCents, savingsCents, percent };
  };

  const getActiveCartPricing = (formId, qtyOverride, cartOverride) => {
    if (!isDiscountIntentOn()) {
      window.__yqLastDiscountPreview = null;
      return null;
    }
    const cart = cartOverride || lastCart;
    const code = getManagedCode();
    if (!code) {
      window.__yqLastDiscountPreview = null;
      return null;
    }
    // Need a live or cached rule — not necessarily the code currently on cart.
    if (!resolveDiscountRule(cart, code)) {
      window.__yqLastDiscountPreview = null;
      return null;
    }

    const qty = qtyOverride != null ? qtyOverride : getFormQuantity(formId);
    const pricing = deriveDisplayPricing(formId, qty, cart);
    window.__yqLastDiscountPreview = pricing;
    return pricing;
  };

  const buildBuyRowPricing = (formId, cartOverride, unitPricingOverride) => {
    if (!isDiscountIntentOn()) return null;
    const id = formId || getBuyTotalsFormId();
    const qty = getFormQuantity(id);
    // Always reuse the same unit pricing as the top PDP (qty=1), then scale.
    const unit =
      unitPricingOverride ||
      getActiveCartPricing(id, 1, cartOverride || lastCart);
    if (!unit || unit.savingsCents <= 0) return null;

    const pricing =
      qty === 1
        ? unit
        : getActiveCartPricing(id, qty, cartOverride || lastCart) || {
            saleCents: unit.saleCents * qty,
            originalCents: unit.originalCents * qty,
            savingsCents: unit.savingsCents * qty,
            percent: unit.percent
          };
    if (!pricing || pricing.savingsCents <= 0) return null;

    const { addonPrice, addonCompare } = getAddonTotals(id);
    const saleCents = pricing.saleCents + addonPrice;
    const originalCents = pricing.originalCents + addonCompare;
    const savingsCents = Math.max(0, originalCents - saleCents);
    const percent = originalCents > 0 ? Math.round((savingsCents / originalCents) * 100) : 0;
    if (savingsCents <= 0) return null;

    return { saleCents, originalCents, savingsCents, percent };
  };

  const adjustBuyTotalsForCoupon = (totals) => {
    const formId = getBuyTotalsFormId();
    const pricing = buildBuyRowPricing(formId, lastCart);

    if (!pricing) {
      if (!isDiscountIntentOn()) window.__yqLastDiscountPreview = null;
      return totals;
    }

    window.__yqLastDiscountPreview = pricing;

    return {
      totalPrice: pricing.saleCents,
      totalCompare: pricing.originalCents,
      savings: pricing.savingsCents,
      percent: pricing.percent
    };
  };

  const formatMoney = (cents) => {
    if (typeof window.yqFormatMoney === 'function') return window.yqFormatMoney(cents);
    const settings = window.themeVariables?.settings || {};
    const format = (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat) || '${{amount}}';
    const abs = Math.max(0, Math.round(Number(cents) || 0));
    const value = (abs / 100).toFixed(2);
    const [whole, fraction = '00'] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*amount\s*\}\}/g, `${withCommas}.${fraction}`);
  };

  const formatSaveBadge = (savingsCents) => {
    if (typeof window.yqFormatSaveBadge === 'function') return window.yqFormatSaveBadge(savingsCents);
    const template = window.themeVariables?.strings?.discountBadgeHtml || 'Save %%SAVINGS%%';
    return template.replace('%%SAVINGS%%', formatMoney(savingsCents));
  };

  const setPriceNodeText = (node, formatted) => {
    if (typeof window.yqSetPriceNodeText === 'function') {
      window.yqSetPriceNodeText(node, formatted);
      return;
    }
    if (!node) return;
    const srOnly = node.querySelector(':scope > .sr-only');
    node.textContent = '';
    if (srOnly) node.appendChild(srOnly);
    node.append(` ${formatted}`);
  };

  const ensurePercentBadge = (anchorEl) => {
    if (!anchorEl) return null;
    let badge = anchorEl.parentElement?.querySelector('.yq-discount-pct-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'yq-discount-pct-badge';
      anchorEl.after(badge);
    }
    return badge;
  };

  const ensurePriceBreak = (rootEl, afterEl) => {
    let br = rootEl.querySelector('.yq-discount-price-break');
    if (!br) {
      br = document.createElement('span');
      br.className = 'yq-discount-price-break';
      br.setAttribute('aria-hidden', 'true');
    }
    const pct = rootEl.querySelector('.yq-discount-pct-badge');
    const host = pct || afterEl;
    if (host && br.previousElementSibling !== host) {
      host.after(br);
    }
    return br;
  };

  const moveCompareBeforeSale = (salePrice, compareAt) => {
    if (!salePrice || !compareAt || !salePrice.parentElement) return;
    if (salePrice.previousElementSibling !== compareAt) {
      salePrice.parentElement.insertBefore(compareAt, salePrice);
    }
  };

  const snapshotPriceRoot = (rootEl) => {
    if (!rootEl || rootEl.dataset.yqPriceSnapshot) return;
    // A later refresh must not freeze the already-discounted DOM as the catalog snapshot.
    if (rootEl.dataset.yqDiscountApplied === 'true') return;
    const target = rootEl.querySelector('.rating-with-text') || rootEl;
    rootEl.dataset.yqPriceSnapshot = target.innerHTML;
  };

  const restorePriceRoot = (rootEl) => {
    rootEl.classList.remove('is-yq-discount-on');
    rootEl.querySelector('.yq-discount-pct-badge')?.remove();
    rootEl.querySelector('.yq-discount-price-break')?.remove();
    const snapshot = rootEl.dataset.yqPriceSnapshot;
    if (!snapshot) return;
    const target = rootEl.querySelector('.rating-with-text') || rootEl;
    target.innerHTML = snapshot;
    delete rootEl.dataset.yqPriceSnapshot;
    delete rootEl.dataset.yqDiscountApplied;
  };

  const getBuyPriceHost = (rootEl) =>
    rootEl.querySelector('.yq-inline-buy-row__price') ||
    rootEl.querySelector('.yq-pdp-floating-bar__mobile-price') ||
    rootEl.querySelector('.yq-pdp-floating-bar__price-line') ||
    null;

  const snapshotBuyTotals = (rootEl) => {
    if (!rootEl || rootEl.dataset.yqPriceSnapshot) return;
    if (rootEl.dataset.yqDiscountApplied === 'true') return;
    const host = getBuyPriceHost(rootEl);
    if (!host) return;
    rootEl.dataset.yqPriceSnapshot = host.innerHTML;
  };

  const restoreBuyTotals = (rootEl) => {
    rootEl.classList.remove('is-yq-discount-on');
    rootEl.querySelectorAll('.yq-discount-pct-badge, .yq-discount-price-break').forEach((el) => el.remove());
    const snapshot = rootEl.dataset.yqPriceSnapshot;
    const host = getBuyPriceHost(rootEl);
    if (snapshot && host) {
      host.innerHTML = snapshot;
    } else {
      // Fallback: put compare after sale if we had reordered it.
      const salePrice = rootEl.querySelector('sale-price');
      const compareAt = rootEl.querySelector('compare-at-price');
      if (salePrice && compareAt && compareAt.nextElementSibling === salePrice) {
        salePrice.after(compareAt);
      }
    }
    delete rootEl.dataset.yqPriceSnapshot;
    delete rootEl.dataset.yqDiscountApplied;
  };

  const clearBuyTotalsDiscountUi = (rootEl) => {
    restoreBuyTotals(rootEl);
  };

  const updatePriceRoot = (rootEl, saleCents, compareCents, savings, percent) => {
    rootEl.classList.add('is-yq-discount-on');
    rootEl.dataset.yqDiscountApplied = 'true';

    const salePrice = rootEl.querySelector('sale-price');
    let compareAt = rootEl.querySelector('compare-at-price');
    setPriceNodeText(salePrice, formatMoney(saleCents));
    if (salePrice) salePrice.classList.remove('text-on-sale');

    if (!compareAt && salePrice) {
      compareAt = document.createElement('compare-at-price');
      compareAt.className = 'text-subdued line-through';
      compareAt.innerHTML = '<span class="sr-only"></span>';
      salePrice.before(compareAt);
    }
    if (compareAt) {
      setPriceNodeText(compareAt, formatMoney(Math.max(compareCents, saleCents)));
      compareAt.hidden = false;
      compareAt.classList.remove('is-hidden');
      compareAt.style.display = '';
      moveCompareBeforeSale(salePrice, compareAt);
    }

    let pctBadge = rootEl.querySelector('.yq-discount-pct-badge');
    if (percent > 0) {
      pctBadge = ensurePercentBadge(compareAt || salePrice);
      if (pctBadge) {
        pctBadge.textContent = `-${percent}%`;
        pctBadge.hidden = false;
      }
    } else if (pctBadge) {
      pctBadge.hidden = true;
    }

    ensurePriceBreak(rootEl, pctBadge || compareAt || salePrice);

    let badge = rootEl.querySelector('.badge--on-sale');
    const badgeList = rootEl.querySelector(
      '.product-info__badge-list, .yq-inline-buy-row__badge-list, .yq-pdp-floating-bar__mobile-badges'
    );
    if (savings > 0) {
      if (!badge && badgeList) {
        badge = document.createElement('on-sale-badge');
        badge.className = 'badge badge--on-sale';
        badgeList.append(badge);
      }
      if (badge) {
        badge.textContent = formatSaveBadge(savings);
        badge.hidden = false;
        badge.classList.remove('is-hidden');
        badge.style.removeProperty('display');
      }
    } else if (badge) {
      badge.hidden = true;
      badge.classList.add('is-hidden');
      badge.style.display = 'none';
    }
  };

  const updatePdpInfoPrice = (cartOverride, pricingOverride) => {
    const roots = document.querySelectorAll('[data-yq-pdp-price]');
    const formId = getBuyTotalsFormId();
    const pricing =
      pricingOverride ||
      getActiveCartPricing(formId, 1, cartOverride || lastCart);

    roots.forEach((rootEl) => {
      if (!pricing || pricing.savingsCents <= 0) {
        if (rootEl.dataset.yqDiscountApplied === 'true') {
          restorePriceRoot(rootEl);
        }
        return;
      }

      snapshotPriceRoot(rootEl);
      updatePriceRoot(
        rootEl,
        pricing.saleCents,
        pricing.originalCents,
        pricing.savingsCents,
        pricing.percent
      );
    });

    document.querySelectorAll(CARD_SEL).forEach((card) => {
      syncRedeemedCopy(card);
    });

    return pricing;
  };

  const syncRedeemedCopy = (card) => {
    const offer = card.querySelector('[data-yq-discount-offer]');
    const offerSource = card.querySelector('[data-yq-discount-offer-source]');
    const redeemedSource = card.querySelector('[data-yq-discount-redeemed-source]');
    if (!offer) return;
    if (card.classList.contains('is-enabled')) {
      if (redeemedSource) offer.innerHTML = redeemedSource.innerHTML;
    } else if (offerSource) {
      offer.innerHTML = offerSource.innerHTML;
    }
  };

  const updateFloatingDesktopPrice = (rootEl, totalPrice, totalCompare, savings) => {
    const current = rootEl.querySelector('.yq-pdp-floating-bar__price-current');
    let compare = rootEl.querySelector('.yq-pdp-floating-bar__price-compare');
    // Buy-row layout stays sale → compare. The discount class reorders compare first.
    rootEl.classList.remove('is-yq-discount-on');
    if (current) current.textContent = formatMoney(totalPrice);
    if (savings > 0 && totalCompare > totalPrice) {
      if (!compare && current) {
        compare = document.createElement('span');
        compare.className = 'yq-pdp-floating-bar__price-compare';
        current.after(compare);
      } else if (compare && current) {
        current.after(compare);
      }
      if (compare) {
        compare.textContent = formatMoney(totalCompare);
        compare.hidden = false;
        compare.style.display = '';
      }
    } else if (compare) {
      compare.hidden = true;
      compare.style.display = 'none';
    }
  };

  // Prevent re-entrant paint when we wrap theme yqSyncAddonBuyTotals.
  let paintingBuyTotals = false;
  let priceRefreshSuspended = false;
  let nativeYqSyncAddonBuyTotals =
    typeof window.yqSyncAddonBuyTotals === 'function' ? window.yqSyncAddonBuyTotals : null;

  /**
   * Buy / ATC row: keep default layout (no % badge, no reorder).
   * Only rewrite sale / compare / Save dollar amounts.
   */
  const updateBuyTotalsAmountsOnly = (rootEl, saleCents, compareCents, savings) => {
    rootEl.dataset.yqDiscountApplied = 'true';
    rootEl.classList.remove('is-yq-discount-on');
    rootEl.querySelectorAll('.yq-discount-pct-badge, .yq-discount-price-break').forEach((el) => el.remove());

    const salePrice = rootEl.querySelector('sale-price');
    let compareAt = rootEl.querySelector('compare-at-price');

    setPriceNodeText(salePrice, formatMoney(saleCents));
    if (salePrice) {
      salePrice.classList.toggle('text-on-sale', savings > 0 && compareCents > saleCents);
    }

    if (compareCents > saleCents) {
      if (!compareAt && salePrice) {
        compareAt = document.createElement('compare-at-price');
        compareAt.className = 'text-subdued line-through';
        compareAt.innerHTML = '<span class="sr-only"></span>';
        // Default theme order: sale first, then compare.
        salePrice.after(compareAt);
      }
      if (compareAt) {
        setPriceNodeText(compareAt, formatMoney(compareCents));
        compareAt.hidden = false;
        compareAt.classList.remove('is-hidden');
        compareAt.style.display = '';
        // Keep compare after sale (do not move above).
        if (salePrice && compareAt.nextElementSibling === salePrice) {
          salePrice.after(compareAt);
        }
      }
    } else if (compareAt) {
      compareAt.hidden = true;
      compareAt.classList.add('is-hidden');
      compareAt.style.display = 'none';
    }

    let badge = rootEl.querySelector('.badge--on-sale');
    const badgeList = rootEl.querySelector(
      '.yq-inline-buy-row__badge-list, .yq-pdp-floating-bar__mobile-badges, .product-info__badge-list'
    );
    if (savings > 0) {
      if (!badge && badgeList) {
        badge = document.createElement('on-sale-badge');
        badge.className = 'badge badge--on-sale';
        badgeList.append(badge);
      }
      if (badge) {
        badge.textContent = formatSaveBadge(savings);
        badge.hidden = false;
        badge.classList.remove('is-hidden');
        badge.style.removeProperty('display');
      }
    } else if (badge) {
      badge.hidden = true;
      badge.classList.add('is-hidden');
      badge.style.display = 'none';
    }

    // Floating desktop line: amounts only, no layout class.
    const desktopCurrent = rootEl.querySelector('.yq-pdp-floating-bar__price-current');
    let desktopCompare = rootEl.querySelector('.yq-pdp-floating-bar__price-compare');
    if (desktopCurrent) {
      desktopCurrent.textContent = formatMoney(saleCents);
      if (savings > 0 && compareCents > saleCents) {
        if (!desktopCompare) {
          desktopCompare = document.createElement('span');
          desktopCompare.className = 'yq-pdp-floating-bar__price-compare';
          desktopCurrent.after(desktopCompare);
        }
        desktopCompare.textContent = formatMoney(compareCents);
        desktopCompare.hidden = false;
        desktopCompare.style.display = '';
      } else if (desktopCompare) {
        desktopCompare.hidden = true;
        desktopCompare.style.display = 'none';
      }
    }
  };

  const applyDiscountUiToBuyTotals = (rootEl, formId, cartOverride, unitPricing) => {
    const id = formId || rootEl.getAttribute('data-product-form-id') || getBuyTotalsFormId();

    if (!isDiscountIntentOn()) {
      restoreBuyTotals(rootEl);
      return;
    }

    const pricing = buildBuyRowPricing(id, cartOverride || lastCart, unitPricing);
    if (!pricing) {
      restoreBuyTotals(rootEl);
      return;
    }

    snapshotBuyTotals(rootEl);
    window.__yqLastDiscountPreview = pricing;
    updateBuyTotalsAmountsOnly(
      rootEl,
      pricing.saleCents,
      pricing.originalCents,
      pricing.savingsCents
    );
  };

  const refreshBuyTotals = (formId, cartOverride, unitPricing) => {
    if (paintingBuyTotals) return;
    paintingBuyTotals = true;
    try {
      const preferredId = getBuyTotalsFormId(formId);
      const roots = document.querySelectorAll('[data-yq-buy-totals]');
      const cart = cartOverride || lastCart;

      if (!isDiscountIntentOn()) {
        roots.forEach((rootEl) => restoreBuyTotals(rootEl));
        if (preferredId && typeof nativeYqSyncAddonBuyTotals === 'function') {
          nativeYqSyncAddonBuyTotals(preferredId);
        }
        window.__yqLastDiscountPreview = null;
        return;
      }

      roots.forEach((rootEl) => {
        const id = rootEl.getAttribute('data-product-form-id') || preferredId || '';
        applyDiscountUiToBuyTotals(rootEl, id, cart, unitPricing);
      });
    } finally {
      paintingBuyTotals = false;
    }
  };

  let quickBuyBases = null;

  const basesFromVariant = (variant) => {
    if (!variant) return null;
    const price = Math.round(Number(variant.price));
    if (!Number.isFinite(price)) return null;
    let compare = Math.round(Number(variant.compare_at_price));
    if (!Number.isFinite(compare) || compare < price) compare = price;
    return { basePrice: price, baseCompare: compare };
  };

  const isQuickBuyEvent = (event) => {
    const formId = event?.detail?.formId || event?.target?.id || '';
    if (String(formId).includes('quick-buy')) return true;
    const target = event?.target;
    if (target?.closest?.('.yq-pdp-floating-quick-buy, quick-buy-drawer')) return true;
    return false;
  };

  const readQuickBuyBasesFromDom = () => {
    const node = document.querySelector('.yq-pdp-floating-quick-buy [data-yq-quick-buy-price]');
    if (!node) return null;
    const price = parseInt(node.getAttribute('data-base-price'), 10);
    if (!Number.isFinite(price)) return null;
    let compare = parseInt(node.getAttribute('data-base-compare'), 10);
    if (!Number.isFinite(compare) || compare < price) compare = price;
    return { basePrice: price, baseCompare: compare };
  };

  const paintQuickBuyFromSelection = () => {
    const fromDom = readQuickBuyBasesFromDom();
    if (fromDom) quickBuyBases = fromDom;
    document.querySelectorAll(QUICK_BUY_PRICE_SEL).forEach((el) => {
      delete el.dataset.yqPriceSnapshot;
    });
    refreshQuickBuyPrices(lastCart, pricingForBases(quickBuyBases));
  };

  const pricingForBases = (bases) => {
    if (!bases) return null;
    const previous = frozenCatalogBases;
    frozenCatalogBases = bases;
    const pricing = deriveDisplayPricing(getBuyTotalsFormId(), 1, lastCart);
    frozenCatalogBases = previous;
    return pricing;
  };

  const QUICK_BUY_PRICE_SEL = '.yq-pdp-floating-quick-buy .quick-buy-drawer__price-line, .yq-pdp-floating-quick-buy .quick-buy-drawer__buy-price';

  const refreshQuickBuyPrices = (cartOverride, unitPricing) => {
    const roots = document.querySelectorAll(QUICK_BUY_PRICE_SEL);
    if (!roots.length) return;
    const id = getBuyTotalsFormId();
    const pricing = pricingForBases(quickBuyBases) || unitPricing || getActiveCartPricing(id, 1, cartOverride || lastCart);
    roots.forEach((rootEl) => {
      if (!isDiscountIntentOn() || !pricing || pricing.savingsCents <= 0) {
        if (rootEl.dataset.yqDiscountApplied === 'true') restorePriceRoot(rootEl);
        return;
      }
      snapshotPriceRoot(rootEl);
      updateBuyTotalsAmountsOnly(
        rootEl,
        pricing.saleCents,
        pricing.originalCents,
        pricing.savingsCents
      );
    });
  };

  const refreshPrices = async (formId, cartOverride) => {
    const epoch = ++priceRefreshEpoch;
    if (cartOverride) {
      rememberCart(cartOverride);
    } else if (!lastCart) {
      try {
        await fetchCart();
      } catch (e) { /* ignore */ }
    }
    if (epoch !== priceRefreshEpoch) return;

    const id = getBuyTotalsFormId(formId);
    const cart = cartOverride || lastCart;
    const unitPricing = getActiveCartPricing(id, 1, cart);
    updatePdpInfoPrice(cart, unitPricing);
    if (epoch !== priceRefreshEpoch) return;
    refreshBuyTotals(id, cart, unitPricing);
    refreshQuickBuyPrices(cart, unitPricing);
  };

  /** Coalesce bursty cart:change / cart:refresh / toggle callbacks into one paint. */
  const schedulePriceRefresh = (formId, cartOverride) => {
    if (priceRefreshSuspended) {
      if (cartOverride) rememberCart(cartOverride);
      return;
    }
    if (cartOverride) rememberCart(cartOverride);
    const id = getBuyTotalsFormId(formId);
    window.clearTimeout(priceRefreshTimer);
    priceRefreshTimer = window.setTimeout(() => {
      refreshPrices(id, lastCart);
    }, 80);
  };

  // Theme sync (addons / qty / product:rerender) often rewrites catalog prices after us —
  // always re-paint coupon totals on top. theme.js is type=module so wrap may need a retry.
  const ensureSyncWrapped = () => {
    if (typeof window.yqSyncAddonBuyTotals !== 'function') return;
    if (window.yqSyncAddonBuyTotals.__yqCouponWrapped) {
      nativeYqSyncAddonBuyTotals = nativeYqSyncAddonBuyTotals || window.yqSyncAddonBuyTotals;
      return;
    }
    nativeYqSyncAddonBuyTotals = window.yqSyncAddonBuyTotals;
    const wrappedSync = (formId) => {
      nativeYqSyncAddonBuyTotals(formId);
      if (!paintingBuyTotals && isDiscountIntentOn()) {
        refreshBuyTotals(formId, lastCart);
      }
    };
    wrappedSync.__yqCouponWrapped = true;
    window.yqSyncAddonBuyTotals = wrappedSync;
  };
  ensureSyncWrapped();

  const setCardUi = (card, { enabled, otherCode }) => {
    const button = card.querySelector(BUTTON_SEL);
    const label = card.querySelector('[data-yq-discount-switch-label]');
    card.classList.toggle('is-enabled', !!enabled);
    card.classList.toggle('is-other-code', !!otherCode);
    if (button) {
      button.setAttribute('aria-pressed', enabled ? 'true' : 'false');
      button.setAttribute('aria-label', enabled ? 'On' : (card.dataset.offLabel || 'Redeem voucher'));
    }
    if (label) {
      label.textContent = card.dataset.offLabel || 'Redeem voucher';
    }

    syncRedeemedCopy(card);
  };

  const sessionEnablesCard = (card) => {
    const code = (card?.dataset.code || '').trim();
    if (!code) return false;
    const state = readState();
    return !!(state.enabled && codesEqual(state.code, code));
  };

  // Full page loads (storefront preview) drop in-memory intent. Restore it
  // only when this card's code is the one the shopper turned on.
  const applyRestoredIntent = () => {
    const card = getActiveCard();
    if (!card || card.dataset.busy === 'true') return pageIntentEnabled;
    if (!sessionEnablesCard(card)) return pageIntentEnabled;
    pageIntentEnabled = true;
    if (!frozenCatalogBases) freezeCatalogBases(getProductForm()?.id);
    setCardUi(card, { enabled: true, otherCode: false });
    return true;
  };

  const intentOnForCard = (card) => {
    if (!card) return false;
    if (card.dataset.busy === 'true') return card.classList.contains('is-enabled');
    if (sessionEnablesCard(card)) return true;
    const code = (card.dataset.code || '').trim();
    const state = readState();
    return !!(pageIntentEnabled && code && (!state.code || codesEqual(state.code, code)));
  };

  const syncCardFromCart = (card, cart) => {
    const code = (card.dataset.code || '').trim();
    if (!code || card.dataset.busy === 'true') return;

    // Cart contents must not rewrite the switch. Only this page's saved intent does.
    if (intentOnForCard(card)) {
      pageIntentEnabled = true;
      if (!frozenCatalogBases) freezeCatalogBases(getProductForm()?.id);
      setCardUi(card, { enabled: true, otherCode: false });
      if (cart && cartHasCode(cart, code)) {
        const live = findCodeDiscountRule(cart, code);
        if (live) cacheDiscountRule(code, live);
      }
    } else {
      clearFrozenCatalogBases();
      setCardUi(card, { enabled: false, otherCode: false });
    }

    const form = getProductForm();
    schedulePriceRefresh(form?.id, cart);
  };

  const syncAllCards = async (cart) => {
    if (cartLockDepth > 0) {
      syncAfterLock = true;
      if (cart) rememberCart(cart);
      return;
    }
    const seq = ++syncSeq;
    applyRestoredIntent();
    const resolved = cart || await fetchCart().catch(() => null);
    if (seq !== syncSeq || !resolved) return;
    rememberCart(resolved);
    if (getConflictMessage()) {
      // Conflict banner is legacy; clear once cart has our intended code.
      const state = readState();
      const card = getActiveCard();
      const intentCode = (card?.dataset.code || state.code || '').trim();
      if (!intentCode || cartHasCode(resolved, intentCode)) setConflictMessage(false);
    }
    document.querySelectorAll(`${CARD_SEL}[data-code]`).forEach((card) => {
      syncCardFromCart(card, resolved);
    });
    renderConflictBanners();
  };

  const requestSync = (cart) => {
    if (cart) {
      queuedSyncCart = cart;
      queuedSyncHasCart = true;
      rememberCart(cart);
    }
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => {
      const hasCart = queuedSyncHasCart;
      const next = queuedSyncCart;
      queuedSyncCart = null;
      queuedSyncHasCart = false;
      syncAllCards(hasCart ? next : undefined);
    }, 40);
  };

  const clearCartDiscounts = () => withCartLock(async () => {
    const cart = await updateCartDiscount('');
    writeState({ managedCode: null, enabled: false });
    pageIntentEnabled = false;
    clearFrozenCatalogBases();
    setConflictMessage(false);
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    return cart;
  });

  const removeManagedOrSelfCode = async (code) => {
    const cart = await fetchCart();
    const state = readState();
    const removeSet = [code, state.managedCode].filter(Boolean);
    const remaining = listedCodes(cart).filter(
      (c) => !removeSet.some((item) => codesEqual(c, item))
    );
    const next = remaining[0] || '';
    const updated = await updateCartDiscount(next);
    writeState({ managedCode: null, enabled: false });
    dispatchCartEvents(updated, 'yq-discount-remove');
    return updated;
  };

  /**
   * Apply the PDP toggle code at add-to-cart time, overwriting any previous code.
   */
  const afterAddToCart = () => withCartLock(async () => {
    const state = readState();
    const card = getActiveCard();
    const intentCode = (card?.dataset.code || state.code || '').trim();
    const intentEnabled = card ? card.classList.contains('is-enabled') : !!state.enabled;

    if (!intentEnabled || !intentCode) {
      setConflictMessage(false);
      return null;
    }

    // Last add-to-cart wins: clear every code already on the cart, then apply this product's code.
    await updateCartDiscount('', { bundleSections: false });
    const updated = await updateCartDiscount(intentCode);
    const ok = cartHasCode(updated, intentCode);
    if (ok) {
      const live = findCodeDiscountRule(updated, intentCode);
      if (live) cacheDiscountRule(intentCode, live);
      writeState({ enabled: true, code: intentCode, managedCode: intentCode });
      setConflictMessage(false);
      pageIntentEnabled = true;
      if (!frozenCatalogBases) freezeCatalogBases(getProductForm()?.id);
      if (card) setCardUi(card, { enabled: true, otherCode: false });
      dispatchCartEvents(updated, 'yq-discount-apply');
    } else {
      // Code rejected by Shopify (not eligible) — local switch stays usable next try.
      setConflictMessage(false);
      if (card) setCardUi(card, { enabled: true, otherCode: false });
    }
    const form = getProductForm();
    schedulePriceRefresh(form?.id, updated);
    return updated;
  });

  const onToggle = async (card) => {
    const code = (card.dataset.code || '').trim();
    if (!code || card.dataset.busy === 'true') return;
    card.dataset.busy = 'true';
    const button = card.querySelector(BUTTON_SEL);
    if (button) button.setAttribute('aria-busy', 'true');
    const form = getProductForm();
    const formId = form?.id || getBuyTotalsFormId();

    try {
      await withCartLock(async () => {
        const turningOn = !card.classList.contains('is-enabled');
        if (turningOn) {
          priceRefreshSuspended = true;
          window.clearTimeout(priceRefreshTimer);
          freezeCatalogBases(formId);
          pageIntentEnabled = true;
          setCardUi(card, { enabled: true, otherCode: false });
          writeState({ enabled: true, code });
          setConflictMessage(false);

          // Do not leave this code permanently on the cart — only probe to learn
          // the rule for PDP pricing. Previous cart codes stay until ATC overwrite.
          const cart = await fetchCart();
          let rule = resolveDiscountRule(cart, code);
          if (!rule) {
            rule = await probeAndCacheDiscountRule(code);
          }
          const workingCart = await fetchCart().catch(() => lastCart);
          rememberCart(workingCart);
          await refreshPrices(formId, workingCart);
        } else {
          priceRefreshSuspended = true;
          window.clearTimeout(priceRefreshTimer);
          pageIntentEnabled = false;
          setCardUi(card, { enabled: false, otherCode: false });
          writeState({ enabled: false, code });
          clearFrozenCatalogBases();
          window.__yqLastDiscountPreview = null;
          // Restore catalog prices immediately (PDP snapshot + buy-row snapshot).
          await refreshPrices(formId, lastCart);

          // Only remove this page's code if it is currently on the cart and managed
          // by us (applied via a prior ATC). Never touch unrelated codes.
          const cart = await fetchCart();
          const state = readState();
          const shouldRemove =
            cartHasCode(cart, code) &&
            state.managedCode &&
            codesEqual(state.managedCode, code);
          if (shouldRemove) {
            const updated = await removeManagedOrSelfCode(code);
            rememberCart(updated);
            await refreshPrices(formId, updated);
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          } else {
            await refreshPrices(formId, cart);
          }
        }
      });
    } finally {
      priceRefreshSuspended = false;
      card.dataset.busy = 'false';
      if (button) button.removeAttribute('aria-busy');
    }
  };

  const bindCard = (card) => {
    if (!card || card.dataset.yqDiscountBound === 'true') return;
    card.dataset.yqDiscountBound = 'true';
    const button = card.querySelector(BUTTON_SEL);
    if (button) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        onToggle(card);
      });
    }
  };

  const initCards = () => {
    document.querySelectorAll(CARD_SEL).forEach(bindCard);
  };

  const renderConflictBanners = () => {
    const message = getConflictMessage();
    document.querySelectorAll('[data-yq-coupon-conflict]').forEach((el) => {
      if (message) {
        el.hidden = false;
        const text = el.querySelector('[data-yq-coupon-conflict-text]');
        if (text) text.textContent = message;
      } else {
        el.hidden = true;
      }
    });
  };

  window.yqDiscountToggle = {
    getActiveCartPricing,
    adjustBuyTotalsForCoupon,
    afterAddToCart,
    refreshPrices,
    updateFloatingDesktopPrice,
    syncAllCards,
    renderConflictBanners,
    clearCartDiscounts,
    CONFLICT_MESSAGE
  };

  window.yqCouponAfterAddToCart = afterAddToCart;
  window.yqCouponAdjustBuyTotals = adjustBuyTotalsForCoupon;
  window.yqCouponUpdateFloatingDesktopPrice = updateFloatingDesktopPrice;

  document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON_SEL);
    if (!button) return;
    const card = button.closest(CARD_SEL);
    if (!card || card.dataset.yqDiscountBound === 'true') return;
    bindCard(card);
    onToggle(card);
  });

  document.addEventListener('yq:quick-buy-ready', () => {
    quickBuyBases = null;
    paintQuickBuyFromSelection();
  });

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail?.cart;
    if (!cart) return;
    rememberCart(cart);

    const baseEvent = event.detail?.baseEvent || '';
    const terminal = baseEvent === 'yq-discount-apply' || baseEvent === 'yq-discount-remove' || baseEvent === 'yq-discount-follow';
    // Ignore intermediate cart payloads while a probe or ATC write is in flight.
    if (cartLockDepth > 0 && !terminal) return;

    if (cart.item_count === 0) {
      // The switch is a page-level price preview. An empty cart must not turn it off.
      if (terminal || pageIntentEnabled || sessionEnablesCard(getActiveCard())) {
        requestSync(cart);
        return;
      }

      // No toggle intent: drop a code left on an empty cart, and leave saved intent alone.
      if (listedCodes(cart).length > 0) {
        withCartLock(async () => {
          if (pageIntentEnabled || readState().enabled) return;
          const updated = await updateCartDiscount('');
          writeState({ managedCode: null });
          setConflictMessage(false);
          rememberCart(updated);
        }).catch(() => {});
      }
      requestSync(cart);
      return;
    }

    requestSync(cart);
  });

  document.addEventListener('cart:refresh', () => {
    if (cartLockDepth > 0) {
      syncAfterLock = true;
      return;
    }
    requestSync();
  });

  document.addEventListener('product:rerender', () => {
    window.setTimeout(() => {
      initCards();
      applyRestoredIntent();
      if (cartLockDepth > 0) return;
      requestSync();
    }, 0);
  });

  document.addEventListener('change', (event) => {
    const quantityInput = event.target?.closest?.('.quantity-selector__input, [name="quantity"]');
    if (!quantityInput) return;
    const form = quantityInput.form || quantityInput.closest('form') || document.getElementById(quantityInput.getAttribute('form'));
    if (form?.id) {
      schedulePriceRefresh(form.id);
    }
  });

  const livePdpBases = () => {
    const root = document.querySelector('.product-info [data-yq-pdp-price]')
      || document.querySelector('[data-yq-pdp-price]');
    if (!root) return null;
    const basePrice = parseInt(root.getAttribute('data-base-price'), 10);
    if (!Number.isFinite(basePrice)) return null;
    let baseCompare = parseInt(root.getAttribute('data-base-compare'), 10);
    if (!Number.isFinite(baseCompare) || baseCompare < basePrice) baseCompare = basePrice;
    return { basePrice, baseCompare };
  };

  const sameBases = (a, b) => !!a && !!b && a.basePrice === b.basePrice && a.baseCompare === b.baseCompare;

  // New SKU HTML must become the catalog snapshot. Keeping the previous
  // snapshot makes the next paint restore the previous variant's price.
  const adoptVariantBases = (bases) => {
    if (!bases || !Number.isFinite(bases.basePrice)) return false;
    const changed = !sameBases(frozenCatalogBases, bases);
    frozenCatalogBases = { basePrice: bases.basePrice, baseCompare: bases.baseCompare };
    document.querySelectorAll('[data-yq-pdp-price], [data-yq-buy-totals]').forEach((el) => {
      el.setAttribute('data-base-price', String(bases.basePrice));
      el.setAttribute('data-base-compare', String(bases.baseCompare));
      if (!changed) return;
      delete el.dataset.yqPriceSnapshot;
      delete el.dataset.yqDiscountApplied;
      el.classList.remove('is-yq-discount-on');
      const salePrice = el.querySelector('sale-price');
      if (salePrice) setPriceNodeText(salePrice, formatMoney(bases.basePrice));
      const compareAt = el.querySelector('compare-at-price');
      if (compareAt && bases.baseCompare > bases.basePrice) {
        setPriceNodeText(compareAt, formatMoney(bases.baseCompare));
      }
    });
    return changed;
  };

  const repaintVariantPrices = (bases) => {
    if (!isDiscountIntentOn()) return;
    const source = bases || livePdpBases();
    if (!source) return;
    adoptVariantBases(source);
    refreshPrices(getBuyTotalsFormId(), lastCart);
  };

  document.addEventListener('variant:change', (event) => {
    const bases = basesFromVariant(event.detail?.variant);
    if (!bases) return;

    if (isQuickBuyEvent(event)) {
      const paint = () => paintQuickBuyFromSelection();
      paint();
      window.requestAnimationFrame(paint);
      return;
    }

    repaintVariantPrices(bases);
    // Theme rerender writes catalog prices on a timer; paint again after that.
    window.requestAnimationFrame(() => repaintVariantPrices(bases));
  });

  // Partial rerender swaps in catalog HTML. Repaint after that swap so the
  // discounted price follows the new SKU instead of the previous snapshot.
  document.addEventListener('product:rerender', (event) => {
    if (isQuickBuyEvent(event)) {
      window.requestAnimationFrame(() => {
        initCards();
        applyRestoredIntent();
        paintQuickBuyFromSelection();
      });
      return;
    }

    window.requestAnimationFrame(() => {
      initCards();
      applyRestoredIntent();
      if (!isDiscountIntentOn()) return;
      repaintVariantPrices(livePdpBases());
    });
  });

  const ensureRuleForIntent = () => withCartLock(async () => {
    if (!pageIntentEnabled) return;
    const code = (getActiveCard()?.dataset.code || '').trim();
    if (!code || resolveDiscountRule(lastCart, code)) return;
    await probeAndCacheDiscountRule(code);
    await refreshPrices(getBuyTotalsFormId(), lastCart);
  });

  const boot = () => {
    ensureSyncWrapped();
    initCards();
    applyRestoredIntent();
    syncAllCards().then(() => {
      if (!pageIntentEnabled) return;
      const code = (getActiveCard()?.dataset.code || '').trim();
      if (code && !resolveDiscountRule(lastCart, code)) ensureRuleForIntent();
    });
    renderConflictBanners();
  };

  document.addEventListener('shopify:section:load', () => {
    initCards();
    applyRestoredIntent();
    refreshPrices(getBuyTotalsFormId(), lastCart);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('load', () => {
    ensureSyncWrapped();
    applyRestoredIntent();
    refreshPrices(getBuyTotalsFormId(), lastCart);
    renderConflictBanners();
  });
})();
